'use strict';

define('vb/action/builtin/callChainAction',[
  'vb/action/action',
  'vb/private/action/actionChainRunner',
  'vb/private/utils',
  'vb/private/log',
],
(Action, ActionChainRunner, Utils, Log) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  /**
   * Represents an action that invokes another action chain.
   *
   * @extends Action
   * @category Action
   */
  class CallChainAction extends Action {
    constructor(id, label) {
      super(id, label);
      this.log = Log.getLogger('/vb/action/action/CallChainAction');
      this.scopes = null;
      this.context = null;
    }

    /**
     * Performs the action by invoking another action chain.
     *
     * @param {Object} parameters - The parameters required to perform the action.
     * @param {string} parameters.id - The ID of the action chain to invoke.
     * @param {string} parameters.chain - The chain to invoke (alternative to `id`).
     * @param {Object} parameters.params - The parameters to pass to the invoked action chain.
     *
     * @returns {Promise<Outcome>} A promise that resolves with the outcome of the invoked action chain.
     *
     * @throws {Error} If the `id` parameter is missing.
     */
    perform(parameters) {
      return new Promise((resolve, reject) => {
        const { id, chain, params } = parameters;
        const actionId = id || chain;

        if (!actionId) {
          this.log.error('Required \'id\' parameter missing.');
          reject();
        }

        this.log.info('Calling action chain', actionId, 'with parameters:', params);

        // We need to create a new context using targetContainer (which is the current container) as the
        // calling container for action chain to be invoked. For example, when a page chain calls a flow chain,
        // the calling container is the page and the target container is the flow. From the flow chain’s perspective,
        // the target container would be the current container when executing the chain. Now, the same flow chain
        // then calls another flow chain, it needs to set up the calling container using the current container
        // which is the target container of the calling context.
        const container = this.context.targetContainer;
        const newContext = {
          application: container.application,
          parent: container.parent,
          container,
          targetScopeResolver: this.context.targetScopeResolver,
          originalContext: this.context.originalContext, // pass along the original context
        };

        ActionChainRunner.create({ chainId: id, chain })
          .run(params, newContext, this.scopes, {
            rethrowError: true,
            coerceResultToOutcome: true,
            coerceErrorToOutcome: true,
            skipParameterEvaluation: true, // prevent double evaluation since parameters have already been evaluated
          })
          .catch(reject)
          .then(resolve);
      });
    }

    /**
     * Sets the scopes and context for the CallChainAction instance.
     *
     * @param {Object} scopes - The scopes associated with the action chain.
     * @param {Object} context - The context in which the action chain will be executed.
     */
    setContext(scopes, context) {
      this.scopes = scopes;
      this.context = context;
    }
  }

  return CallChainAction;
});

